import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  ElementRef,
  NgZone,
  OnDestroy,
  Optional,
  ViewEncapsulation,
  forwardRef,
} from "@angular/core";
import { DateAdapter, MAT_DATE_FORMATS } from "@angular/material/core";
import { MatCalendar, MatCalendarHeader, MatDatepickerIntl } from "@angular/material/datepicker";
import { Translator } from "../../services/translator/translator.service";
import {
  attachMatDatepickerContentResizeReposition,
  scheduleMatDatepickerPopupReposition,
} from "../../utils/mat-datepicker-popup-reposition.util";

@Component({
  selector: "app-filter-calendar-header",
  templateUrl: "./filter-calendar-header.component.html",
  styleUrls: ["./filter-calendar-header.component.scss"],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FilterCalendarHeaderComponent
  extends MatCalendarHeader<any>
  implements AfterViewInit, OnDestroy
{
  private detachResizeReposition?: () => void;
  private static readonly _YEARS_PER_PAGE = 24;
  private static readonly _MIN_NAV_YEAR = 1;
  private static readonly _MAX_NAV_YEAR = 9999;

  /** Na vista multi-ano as setas paginam blocos inteiros de anos; mostrar sempre. */
  showPeriodNavArrows(): boolean {
    return true;
  }

  constructor(
    private intl: MatDatepickerIntl,
    @Inject(forwardRef(() => MatCalendar)) calendar: MatCalendar<any>,
    private dateAdapter: DateAdapter<any>,
    @Optional() @Inject(MAT_DATE_FORMATS) private dateFormats: any,
    private cdr: ChangeDetectorRef,
    private translator: Translator,
    private elementRef: ElementRef<HTMLElement>,
    private ngZone: NgZone
  ) {
    super(intl, calendar, dateAdapter, dateFormats, cdr);
  }

  ngAfterViewInit(): void {
    this.detachResizeReposition = attachMatDatepickerContentResizeReposition(
      this.elementRef,
      this.ngZone
    );
  }

  ngOnDestroy(): void {
    this.detachResizeReposition?.();
  }

  setView(view: "month" | "year" | "multi-year"): void {
    this.calendar.currentView = view;
    this.cdr.markForCheck();
    scheduleMatDatepickerPopupReposition(this.ngZone);
  }

  navLabel(key: string, fallback: string): string {
    const v = this.translator.instant(key);
    return !v || v === key ? fallback : v;
  }

  get periodButtonText(): string {
    const view = this.calendar.currentView;
    if (view === "month" && this.dateFormats) {
      return this.dateAdapter
        .format(this.calendar.activeDate, this.dateFormats.display.monthYearLabel)
        .toLocaleUpperCase();
    }
    if (view === "year") {
      return this.dateAdapter.getYearName(this.calendar.activeDate);
    }
    if (view === "multi-year") {
      /** Siempre el rango de la página actual (~24 años), no el min/max global del datepicker (p. ej. 1–9999). */
      return this.multiYearPeriodLabelForPage();
    }
    return "";
  }

  private multiYearPeriodLabelForPage(): string {
    const a = this.dateAdapter;
    const activeYear = a.getYear(this.calendar.activeDate);
    const startingYear = FilterCalendarHeaderComponent.startingYearForPage(
      a,
      this.calendar.minDate,
      this.calendar.maxDate
    );
    const ypp = FilterCalendarHeaderComponent._YEARS_PER_PAGE;
    const offset = ((activeYear - startingYear) % ypp + ypp) % ypp;
    const minYearOfPage = activeYear - offset;
    const maxYearOfPage = minYearOfPage + FilterCalendarHeaderComponent._YEARS_PER_PAGE - 1;
    const minName = a.getYearName(a.createDate(minYearOfPage, 0, 1));
    const maxName = a.getYearName(a.createDate(maxYearOfPage, 0, 1));
    return this.intl.formatYearRange(minName, maxName);
  }

  private static startingYearForPage(
    dateAdapter: DateAdapter<any>,
    minDate: any,
    maxDate: any
  ): number {
    if (maxDate) {
      return dateAdapter.getYear(maxDate) - FilterCalendarHeaderComponent._YEARS_PER_PAGE + 1;
    }
    if (minDate) {
      return dateAdapter.getYear(minDate);
    }
    return 0;
  }

  previousClicked(): void {
    if (this.calendar.currentView === "multi-year") {
      const ypp = FilterCalendarHeaderComponent._YEARS_PER_PAGE;
      this.calendar.activeDate = this.dateAdapter.addCalendarYears(this.calendar.activeDate, -ypp);
      return;
    }
    super.previousClicked();
  }

  nextClicked(): void {
    if (this.calendar.currentView === "multi-year") {
      const ypp = FilterCalendarHeaderComponent._YEARS_PER_PAGE;
      this.calendar.activeDate = this.dateAdapter.addCalendarYears(this.calendar.activeDate, ypp);
      return;
    }
    super.nextClicked();
  }

  previousEnabled(): boolean {
    if (this.calendar.currentView !== "multi-year") {
      return super.previousEnabled();
    }
    const ypp = FilterCalendarHeaderComponent._YEARS_PER_PAGE;
    const activeY = this.dateAdapter.getYear(this.calendar.activeDate);
    if (this.calendar.minDate) {
      const minY = this.dateAdapter.getYear(this.calendar.minDate);
      return activeY - ypp >= minY;
    }
    return activeY - ypp >= FilterCalendarHeaderComponent._MIN_NAV_YEAR;
  }

  nextEnabled(): boolean {
    if (this.calendar.currentView !== "multi-year") {
      return super.nextEnabled();
    }
    const ypp = FilterCalendarHeaderComponent._YEARS_PER_PAGE;
    const activeY = this.dateAdapter.getYear(this.calendar.activeDate);
    if (this.calendar.maxDate) {
      const maxY = this.dateAdapter.getYear(this.calendar.maxDate);
      return activeY + ypp <= maxY;
    }
    return activeY + ypp <= FilterCalendarHeaderComponent._MAX_NAV_YEAR;
  }
}
